import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface AiSuggestion {
  _id?: string;
  "Job ID": string;
  "Hard Filter Decision"?: string;
  "Hard Filter Reasoning"?: string;
  "Budget Guardrail Reasoning"?: string;
  "Weighted Scoring Score"?: string | number;
  "Weighted Scoring Reasoning"?: string;
  "EV Calculator Decision"?: string;
  "EV Calculator Reasoning"?: string;
  "Final Decision"?: string;
  "Final Synthesis"?: string;
}

export interface JobCheckResult {
  status: "matched" | "not_found" | "invalid_url" | "completed" | "queued";
  jobId?: string;
  job?: UpworkJob;
  ai?: AiSuggestion | null;
  hasAnalysis?: boolean;
  message?: string;
}
export interface UpworkJob {
  _id: string;
  "Job ID": string;
  "Published": string | null;
  "Job Title": string;
  "URL"?: string;
  "Job Description"?: string;

  "Country"?: string;
  "City"?: string;
  "Time Zone"?: string;

  "Category"?: string;
  "Category Group"?: string;
  "Skills and Expertise"?: string;
  "Business Industry"?: string;

  "Budget Amount"?: string;
  "Contract Type"?: string;
  "Contractor Tier"?: string;
  "Experience"?: string;

  "Job Status"?: string;
  "Premium"?: any;
  "Positions to Hire"?: number;

  "Phone Verification"?: string;
  "Payment Verification"?: string;

  "Total Applicants"?: number;
  "Total Invited for Interviews"?: number;
  "Total Hired"?: number;
  "Invitations Sent"?: number;
  "Last Viewed by Client"?: string;

  "Active Job Posts"?: number;
  "Avg. Hourly Rate Paid"?: number;
  "Total Money Spent"?: string;
  "Total Contracts"?: number;
  "Client Rating"?: number;
  "Total Hours Billed"?: number;
  "Jobs with Hires"?: number;
  "Number of Reviews"?: number;
  "Current Active Assignments"?: number;
  "Hiring Success Rate"?: number;

  createdAt?: string;
  updatedAt?: string;

  ai?: AiSuggestion | null;
}

export interface JobAnalytics {
  total: number;
  active: number;
  newJobs: number;
  highCompetition: number;
  aiRecommended: number;
  aiRejected: number;
  aiPending: number;
  avgApplicants: number;
}

interface UpworkJobsState {
  jobs: UpworkJob[];
  selectedJob: UpworkJob | null;
  analytics: JobAnalytics | null;

  isFetching: boolean;
  isFetchingDetail: boolean;
  isReanalyzing: boolean;

  fetchJobs: (params?: Record<string, any>) => Promise<void>;
  fetchJobById: (id: string) => Promise<void>;
  fetchAnalytics: () => Promise<void>;
  reanalyzeJob: (id: string) => Promise<void>;
  reanalyzeOpenJobs: () => Promise<void>;
  setSelectedJob: (j: UpworkJob | null) => void;
}

export const useUpworkJobsStore = create<UpworkJobsState>((set, get) => ({
  jobs: [],
  selectedJob: null,
  analytics: null,

  isFetching: false,
  isFetchingDetail: false,
  isReanalyzing: false,

  fetchJobs: async (params = {}) => {
    set({ isFetching: true });
    try {
      const res = await axiosInstance.get<UpworkJob[]>("/upwork-jobs", { params });
      set({ jobs: res.data });

    } catch (err: any) {
      console.error("fetchJobs:", err);
      toast.error(err.response?.data?.message || "Failed to load jobs");
    } finally {
      set({ isFetching: false });
    }
  },

  fetchJobById: async (id) => {
    set({ isFetchingDetail: true });
    try {
      const res = await axiosInstance.get<UpworkJob>(`/upwork-jobs/${id}`);
      set({ selectedJob: res.data });
    } catch (err: any) {
      console.error("fetchJobById:", err);
      toast.error(err.response?.data?.message || "Failed to load job");
    } finally {
      set({ isFetchingDetail: false });
    }
  },

  fetchAnalytics: async () => {
    try {
      const res = await axiosInstance.get<JobAnalytics>("/upwork-jobs/analytics");
      set({ analytics: res.data });
    } catch (err: any) {
      console.error("fetchAnalytics:", err);
    }
  },

  reanalyzeJob: async (id) => {
    set({ isReanalyzing: true });
    try {
      await axiosInstance.post(`/upwork-jobs/${id}/reanalyze`);
      toast.success("Reanalysis queued");

      await get().fetchJobs();
    } catch (err: any) {
      console.error("reanalyzeJob:", err);
      toast.error(err.response?.data?.message || "Failed to queue reanalysis");
    } finally {
      set({ isReanalyzing: false });
    }
  },

  reanalyzeOpenJobs: async () => {
    set({ isReanalyzing: true });
    try {
      const res = await axiosInstance.post(`/upwork-jobs/reanalyze-all`);
      toast.success(`Queued ${res.data.queued} jobs for reanalysis`);
      await get().fetchJobs();
    } catch (err: any) {
      console.error("reanalyzeOpenJobs:", err);
      toast.error(err.response?.data?.message || "Failed to queue reanalysis");
    } finally {
      set({ isReanalyzing: false });
    }
  },

  setSelectedJob: (j) => set({ selectedJob: j }),
}));
